import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Collapse,
  FormControl, InputLabel, MenuItem, Select, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { supabase } from '../../../supabaseClient'

// ── Constants ─────────────────────────────────────────────────────────────────

const SONNET_IN  = 3.00  // $ per 1M input tokens
const SONNET_OUT = 15.00
const HAIKU_IN   = 0.80
const HAIKU_OUT  = 4.00

const FUNCTION_LABELS: Record<string, string> = {
  'gate-category-input':         'Category Review',
  'recommend-topics':            'Topic Recommendations',
  'evaluate-student-topic':      'Student Idea Evaluation',
  'ask-about-topic':             'Topic Q&A',
  'compare-topics':              'Comparative Analysis',
  'generate-demo-topics':        'Demo Topics',
  'generate-project-directions': 'Direction Discovery',
  'step1-finalize':              'Step 1 Finalization',
  'generate-mentor-evaluation':  'Mentor Evaluation',
}

// ── Phase grouping for drill-down ─────────────────────────────────────────────

const PHASE_GROUPS: Array<{ label: string; functions: string[] }> = [
  {
    label: 'Step 1 — Category Selection',
    functions: ['gate-category-input'],
  },
  {
    label: 'Step 1 — Topic Exploration',
    functions: [
      'recommend-topics', 'evaluate-student-topic',
      'ask-about-topic', 'compare-topics',
      'generate-demo-topics',
    ],
  },
  {
    label: 'Step 1 — Direction & Finalization',
    functions: ['generate-project-directions', 'step1-finalize'],
  },
  {
    label: 'Mentor Evaluation',
    functions: ['generate-mentor-evaluation'],
  },
]

function costUSD(model: string, input: number, output: number) {
  const isHaiku = model.includes('haiku')
  const inRate  = isHaiku ? HAIKU_IN  : SONNET_IN
  const outRate = isHaiku ? HAIKU_OUT : SONNET_OUT
  return (input / 1_000_000) * inRate + (output / 1_000_000) * outRate
}

function fmt$(n: number) {
  return n < 0.01 ? '<$0.01' : `$${n.toFixed(2)}`
}

function fmtNum(n: number) {
  return n.toLocaleString()
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface UsageRow {
  id:            string
  project_id:    string
  student_id:    string
  function_name: string
  model:         string
  input_tokens:  number
  output_tokens: number
  created_at:    string
}

interface StudentProfile {
  student_id: string
  first_name: string
  last_name:  string
  grade:      string
}

interface ByFunction {
  function_name: string
  calls:         number
  input_tokens:  number
  output_tokens: number
  cost:          number
}

interface ByStudent {
  student_id:    string
  name:          string
  grade:         string
  calls:         number
  input_tokens:  number
  output_tokens: number
  cost:          number
  last_activity: string
}

// ── Time range options ────────────────────────────────────────────────────────

const RANGES = [
  { label: 'Last 7 days',  days: 7   },
  { label: 'Last 30 days', days: 30  },
  { label: 'Last 90 days', days: 90  },
  { label: 'All time',     days: 0   },
]

// ── Component ─────────────────────────────────────────────────────────────────

export default function TokenUsagePage() {
  const [rows,            setRows]            = useState<UsageRow[]>([])
  const [students,        setStudents]        = useState<Record<string, StudentProfile>>({})
  const [loading,         setLoading]         = useState(true)
  const [error,           setError]           = useState<string | null>(null)
  const [rangeDays,       setRangeDays]       = useState(30)
  const [expandedStudent, setExpandedStudent] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError(null)
      try {
        let query = supabase
          .from('ai_usage_log')
          .select('*')
          .order('created_at', { ascending: false })

        if (rangeDays > 0) {
          const since = new Date(Date.now() - rangeDays * 864e5).toISOString()
          query = query.gte('created_at', since)
        }

        const { data: usage, error: usageErr } = await query
        if (usageErr) throw new Error(usageErr.message)

        // Fetch student profiles for all unique student_ids
        const studentIds = Array.from(new Set((usage ?? []).map(r => r.student_id)))
        const profileMap: Record<string, StudentProfile> = {}
        if (studentIds.length > 0) {
          const { data: profiles } = await supabase
            .from('student_profile')
            .select('student_id, first_name, last_name, grade')
            .in('student_id', studentIds)
          for (const p of profiles ?? []) profileMap[p.student_id] = p
        }

        setRows(usage ?? [])
        setStudents(profileMap)
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [rangeDays])

  // ── Aggregate totals ────────────────────────────────────────────────────────
  const totalInput  = rows.reduce((s, r) => s + r.input_tokens,  0)
  const totalOutput = rows.reduce((s, r) => s + r.output_tokens, 0)
  const totalCost   = rows.reduce((s, r) => s + costUSD(r.model, r.input_tokens, r.output_tokens), 0)
  const totalCalls  = rows.length

  // ── By function ─────────────────────────────────────────────────────────────
  const byFunction: ByFunction[] = Object.values(
    rows.reduce((acc, r) => {
      if (!acc[r.function_name]) acc[r.function_name] = {
        function_name: r.function_name, calls: 0, input_tokens: 0, output_tokens: 0, cost: 0,
      }
      acc[r.function_name].calls++
      acc[r.function_name].input_tokens  += r.input_tokens
      acc[r.function_name].output_tokens += r.output_tokens
      acc[r.function_name].cost          += costUSD(r.model, r.input_tokens, r.output_tokens)
      return acc
    }, {} as Record<string, ByFunction>)
  ).sort((a, b) => b.cost - a.cost)

  // ── By student ──────────────────────────────────────────────────────────────
  const byStudent: ByStudent[] = Object.values(
    rows.reduce((acc, r) => {
      if (!acc[r.student_id]) {
        const p = students[r.student_id]
        acc[r.student_id] = {
          student_id:    r.student_id,
          name:          p ? `${p.first_name} ${p.last_name}` : r.student_id.slice(0, 8),
          grade:         p?.grade ?? '—',
          calls:         0,
          input_tokens:  0,
          output_tokens: 0,
          cost:          0,
          last_activity: r.created_at,
        }
      }
      acc[r.student_id].calls++
      acc[r.student_id].input_tokens  += r.input_tokens
      acc[r.student_id].output_tokens += r.output_tokens
      acc[r.student_id].cost          += costUSD(r.model, r.input_tokens, r.output_tokens)
      if (r.created_at > acc[r.student_id].last_activity) {
        acc[r.student_id].last_activity = r.created_at
      }
      return acc
    }, {} as Record<string, ByStudent>)
  ).sort((a, b) => b.cost - a.cost)

  // ── Engagement intensity: cost per student rank ─────────────────────────────
  const maxCost = byStudent[0]?.cost ?? 1

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1100 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>AI Token Usage</Typography>
          <Typography variant="body2" color="text.secondary">
            Cost analysis and engagement metrics for Ardent Scholar
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time range</InputLabel>
          <Select
            value={rangeDays}
            label="Time range"
            onChange={e => setRangeDays(Number(e.target.value))}
          >
            {RANGES.map(r => (
              <MenuItem key={r.days} value={r.days}>{r.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {loading && <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>}

      {!loading && !error && (
        <>
          {/* ── Summary cards ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 4 }}>
            {[
              { label: 'Total Cost',         value: fmt$(totalCost),        sub: 'estimated at list price' },
              { label: 'AI Calls',           value: fmtNum(totalCalls),     sub: 'requests to Anthropic' },
              { label: 'Input Tokens',       value: fmtNum(totalInput),     sub: 'tokens sent' },
              { label: 'Output Tokens',      value: fmtNum(totalOutput),    sub: 'tokens received' },
              { label: 'Active Students',    value: fmtNum(byStudent.length), sub: 'with AI activity' },
            ].map(c => (
              <Card key={c.label} variant="outlined">
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, my: 0.25 }}>{c.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* ── By function ── */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Cost by AI Function</Typography>
          <Box sx={{ mb: 4, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Function</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Calls</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Input Tokens</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Output Tokens</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Est. Cost</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>% of Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byFunction.map(fn => (
                  <TableRow key={fn.function_name} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {FUNCTION_LABELS[fn.function_name] ?? fn.function_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{fn.function_name}</Typography>
                    </TableCell>
                    <TableCell align="right">{fmtNum(fn.calls)}</TableCell>
                    <TableCell align="right">{fmtNum(fn.input_tokens)}</TableCell>
                    <TableCell align="right">{fmtNum(fn.output_tokens)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt$(fn.cost)}</TableCell>
                    <TableCell align="right">
                      {totalCost > 0 ? `${((fn.cost / totalCost) * 100).toFixed(1)}%` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(totalCalls)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(totalInput)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(totalOutput)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt$(totalCost)}</TableCell>
                  <TableCell align="right">100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* ── By student ── */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Engagement by Student</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Cost is a proxy for AI interaction depth. Click a row to see the phase breakdown.
          </Typography>
          <Box sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 24 }} />
                  <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>AI Calls</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Tokens</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Est. Cost</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Engagement</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byStudent.map(s => {
                  const intensity = maxCost > 0 ? s.cost / maxCost : 0
                  const engagementLabel =
                    intensity > 0.66 ? 'High'   :
                    intensity > 0.33 ? 'Medium' : 'Low'
                  const engagementColor =
                    intensity > 0.66 ? 'success' :
                    intensity > 0.33 ? 'warning' : 'default'
                  const isExpanded = expandedStudent === s.student_id

                  // Build per-function breakdown for this student
                  const studentRows = rows.filter(r => r.student_id === s.student_id)
                  const byFn = studentRows.reduce((acc, r) => {
                    if (!acc[r.function_name]) acc[r.function_name] = { calls: 0, input: 0, output: 0, cost: 0 }
                    acc[r.function_name].calls++
                    acc[r.function_name].input  += r.input_tokens
                    acc[r.function_name].output += r.output_tokens
                    acc[r.function_name].cost   += costUSD(r.model, r.input_tokens, r.output_tokens)
                    return acc
                  }, {} as Record<string, { calls: number; input: number; output: number; cost: number }>)

                  const studentMaxCost = Math.max(...Object.values(byFn).map(v => v.cost), 0.0001)

                  return (
                    <React.Fragment key={s.student_id}>
                      <TableRow
                        hover
                        onClick={() => setExpandedStudent(isExpanded ? null : s.student_id)}
                        sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                      >
                        <TableCell sx={{ py: 0.5, color: 'text.secondary' }}>
                          {isExpanded
                            ? <KeyboardArrowDownIcon fontSize="small" />
                            : <KeyboardArrowRightIcon fontSize="small" />}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.name}</Typography>
                        </TableCell>
                        <TableCell>{s.grade ? `Grade ${s.grade}` : '—'}</TableCell>
                        <TableCell align="right">{fmtNum(s.calls)}</TableCell>
                        <TableCell align="right">{fmtNum(s.input_tokens + s.output_tokens)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt$(s.cost)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: 'grey.200', overflow: 'hidden' }}>
                              <Box sx={{
                                width: `${intensity * 100}%`, height: '100%', borderRadius: 3,
                                bgcolor: intensity > 0.66 ? 'success.main' : intensity > 0.33 ? 'warning.main' : 'grey.400',
                              }} />
                            </Box>
                            <Chip label={engagementLabel} size="small" color={engagementColor as any} sx={{ fontSize: '0.65rem', height: 18 }} />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(s.last_activity).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* ── Drill-down panel ── */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, border: 'none' }}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <Box sx={{ mx: 4, my: 1.5, mb: 2 }}>
                              {PHASE_GROUPS.map(group => {
                                const groupFns = group.functions.filter(fn => byFn[fn])
                                if (groupFns.length === 0) return null
                                const groupCost = groupFns.reduce((s, fn) => s + byFn[fn].cost, 0)
                                const groupCalls = groupFns.reduce((s, fn) => s + byFn[fn].calls, 0)
                                const groupTokens = groupFns.reduce((s, fn) => s + byFn[fn].input + byFn[fn].output, 0)
                                return (
                                  <Box key={group.label} sx={{ mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {group.label}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                        {fmt$(groupCost)} &nbsp;·&nbsp; {fmtNum(groupTokens)} tokens &nbsp;·&nbsp; {groupCalls} calls
                                      </Typography>
                                    </Box>
                                    <Table size="small" sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                                      <TableBody>
                                        {groupFns.map(fn => {
                                          const d = byFn[fn]
                                          const barPct = (d.cost / studentMaxCost) * 100
                                          return (
                                            <TableRow key={fn} sx={{ '& td': { border: 'none', py: 0.5 } }}>
                                              <TableCell sx={{ pl: 1.5, width: 220 }}>
                                                <Typography variant="body2">{FUNCTION_LABELS[fn] ?? fn}</Typography>
                                              </TableCell>
                                              <TableCell align="right" sx={{ color: 'text.secondary', width: 60 }}>
                                                <Typography variant="caption">{d.calls}×</Typography>
                                              </TableCell>
                                              <TableCell align="right" sx={{ color: 'text.secondary', width: 110 }}>
                                                <Typography variant="caption">{fmtNum(d.input + d.output)} tok</Typography>
                                              </TableCell>
                                              <TableCell align="right" sx={{ fontWeight: 600, width: 70 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{fmt$(d.cost)}</Typography>
                                              </TableCell>
                                              <TableCell sx={{ pr: 1.5 }}>
                                                <Box sx={{ width: 80, height: 4, bgcolor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
                                                  <Box sx={{ width: `${barPct}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 2 }} />
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          )
                                        })}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                )
                              })}
                              {/* Functions not in any group */}
                              {(() => {
                                const knownFns = new Set(PHASE_GROUPS.flatMap(g => g.functions))
                                const unknownFns = Object.keys(byFn).filter(fn => !knownFns.has(fn))
                                if (unknownFns.length === 0) return null
                                return (
                                  <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                      Other
                                    </Typography>
                                    <Table size="small" sx={{ bgcolor: 'grey.50', borderRadius: 1, mt: 0.5 }}>
                                      <TableBody>
                                        {unknownFns.map(fn => {
                                          const d = byFn[fn]
                                          return (
                                            <TableRow key={fn} sx={{ '& td': { border: 'none', py: 0.5 } }}>
                                              <TableCell sx={{ pl: 1.5, width: 220 }}><Typography variant="body2">{fn}</Typography></TableCell>
                                              <TableCell align="right" sx={{ color: 'text.secondary', width: 60 }}><Typography variant="caption">{d.calls}×</Typography></TableCell>
                                              <TableCell align="right" sx={{ color: 'text.secondary', width: 110 }}><Typography variant="caption">{fmtNum(d.input + d.output)} tok</Typography></TableCell>
                                              <TableCell align="right" sx={{ fontWeight: 600, width: 70 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>{fmt$(d.cost)}</Typography></TableCell>
                                              <TableCell sx={{ pr: 1.5 }}>
                                                <Box sx={{ width: 80, height: 4, bgcolor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
                                                  <Box sx={{ width: `${(d.cost / studentMaxCost) * 100}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 2 }} />
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          )
                                        })}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                )
                              })()}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
                {byStudent.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No AI activity in this time range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {/* ── Pricing note ── */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Costs estimated at Anthropic list pricing: Claude Sonnet 4.6 — $3.00/M input, $15.00/M output;
              Claude Haiku 4.5 — $0.80/M input, $4.00/M output. Actual billed amounts may differ based on
              your contract tier. Engagement index is relative to highest-usage student in the selected period.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  )
}
